import type { CourseSessionAttendee } from '../types/crm';

export type AttendeePaymentLedgerRow = {
  id: string;
  amount: number;
  method: string;
  paymentDate: string;
  stepType: string;
  payerNotes: string | null;
};

/** Sum of ledger rows, capped at enrollment fee when fee is known. */
export function sumAttendeeLedgerPaid(
  rows: AttendeePaymentLedgerRow[],
  feeCap?: number | null
): number {
  const raw = rows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const capped = Math.max(0, raw);
  if (feeCap != null && Number.isFinite(feeCap) && feeCap > 0) {
    return Math.min(capped, feeCap);
  }
  return capped;
}

export function derivePaymentStatusFromPaid(
  paid: number,
  fee: number
): 'unpaid' | 'half_paid' | 'paid' {
  if (fee > 0 && paid >= fee - 0.005) return 'paid';
  if (paid > 0) return 'half_paid';
  return 'unpaid';
}

export function getAttendeeDisplayPaid(
  attendee: CourseSessionAttendee,
  ledgerRows: AttendeePaymentLedgerRow[],
  fee: number
): number {
  if (ledgerRows.length > 0) return sumAttendeeLedgerPaid(ledgerRows, fee > 0 ? fee : null);
  return Math.min(Math.max(Number(attendee.amountPaid || 0), 0), fee > 0 ? fee : Number.MAX_SAFE_INTEGER);
}

/** Paid total from ledger when present, otherwise the attendee snapshot. */
export function getExistingAttendeePaid(
  attendee: CourseSessionAttendee,
  ledgerRows: AttendeePaymentLedgerRow[],
  fee: number
): number {
  if (ledgerRows.length > 0) return sumAttendeeLedgerPaid(ledgerRows, fee > 0 ? fee : null);
  const cap = fee > 0 ? fee : Number.MAX_SAFE_INTEGER;
  return Math.min(Math.max(Number(attendee.amountPaid || 0), 0), cap);
}

/**
 * When editing an attendee: ledger rows mean the amount field is an incremental payment;
 * without ledger, the field is the target total paid.
 */
export function computeEditPaymentLineAmount(
  payKind: 'unpaid' | 'prepaid' | 'part' | 'full' | 'keep',
  amountEntered: number,
  existingPaid: number,
  fee: number,
  hasLedger: boolean
): number {
  if (payKind === 'keep' || payKind === 'unpaid') return 0;
  // "Paid in full" has no amount field — remainder is fee − already collected.
  if (payKind === 'full') {
    const target = fee > 0 ? fee : amountEntered;
    return Math.max(0, target - existingPaid);
  }
  if (amountEntered <= 0) return 0;
  if (hasLedger) return amountEntered;
  return Math.max(0, amountEntered - existingPaid);
}

/** Split a lump-sum booking payment evenly across seats (cent-accurate). */
export function splitPaymentAcrossSeats(totalAmount: number, seatCount: number): number[] {
  if (seatCount <= 0) return [];
  if (totalAmount <= 0) return Array(seatCount).fill(0);
  const totalCents = Math.round(totalAmount * 100);
  const baseCents = Math.floor(totalCents / seatCount);
  const remainder = totalCents - baseCents * seatCount;
  return Array.from({ length: seatCount }, (_, i) => (baseCents + (i < remainder ? 1 : 0)) / 100);
}

export function capPaidToFee(paid: number, fee: number): number {
  const safe = Math.max(0, paid);
  if (fee > 0) return Math.min(safe, fee);
  return safe;
}

export function isPaidInFullAmount(paid: number, fee: number): boolean {
  return fee > 0 && paid >= fee - 0.005;
}

/** Uncapped sum of ledger rows (for correcting over-payments when the fee changes). */
export function sumAttendeeLedgerRaw(rows: AttendeePaymentLedgerRow[]): number {
  return Math.max(
    0,
    rows.reduce((s, p) => s + Number(p.amount || 0), 0)
  );
}

/**
 * When an attendee was paid in full at `oldFee` and the enrollment fee is corrected to `newFee`,
 * return how much to add (positive) or remove (negative) from the ledger so they stay paid in full.
 */
export function computePaidInFullFeeCorrectionDelta(
  oldFee: number,
  newFee: number,
  paidTowardOldFee: number
): number {
  if (!(oldFee > 0) || !(newFee > 0)) return 0;
  if (Math.abs(newFee - oldFee) < 0.005) return 0;
  if (!isPaidInFullAmount(paidTowardOldFee, oldFee)) return 0;
  return newFee - paidTowardOldFee;
}
